import { useEffect, useRef, useState } from 'react';
import { ApiError, Button, api, brl, haptic, km, minutes } from '@mobi/web';
import type { RideOffer } from '../useDriver.js';

/**
 * Oferta de corrida em tela cheia, com contagem regressiva.
 *
 * Tela cheia de propósito: o motorista tem 15 segundos e está dirigindo.
 * Um card discreto no canto seria perdido.
 */
export function OfferModal({
  offer,
  onResolved,
}: {
  offer: RideOffer;
  onResolved: () => void;
}) {
  const [left, setLeft] = useState(offer.expiresInMs);
  const [busy, setBusy] = useState<'accept' | 'decline' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const alerted = useRef(false);

  useEffect(() => {
    if (!alerted.current) {
      alerted.current = true;
      haptic([0, 120, 80, 120]);
    }
    const started = Date.now();
    const timer = setInterval(() => {
      const remaining = offer.expiresInMs - (Date.now() - started);
      setLeft(remaining);
      // Não decide nada no cliente: quando o tempo acaba, quem marca a
      // oferta como perdida é o servidor. A tela só sai da frente.
      if (remaining <= 0) {
        clearInterval(timer);
        onResolved();
      }
    }, 100);
    return () => clearInterval(timer);
  }, [offer, onResolved]);

  async function respond(action: 'accept' | 'decline') {
    setBusy(action);
    setError(null);
    try {
      await api(`/api/rides/${offer.rideId}/${action}`, { method: 'POST', body: {} });
      haptic();
      onResolved();
    } catch (err) {
      // Outro motorista foi mais rápido, ou a oferta expirou: sai da tela
      // em vez de deixar o motorista brigando com um botão morto.
      if (err instanceof ApiError && err.status === 409) onResolved();
      else setError(err instanceof ApiError ? err.message : 'não foi possível responder');
    } finally {
      setBusy(null);
    }
  }

  const seconds = Math.max(0, Math.ceil(left / 1000));
  const pct = Math.max(0, Math.min(100, (left / offer.expiresInMs) * 100));

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-surface">
      <div className="h-1.5 w-full bg-surface-2">
        <div
          className="h-full bg-brand transition-[width] duration-100 ease-linear"
          style={{ width: `${pct}%` }}
        />
      </div>

      <div className="flex min-h-0 flex-1 flex-col justify-center px-6 py-8">
        <div className="flex items-baseline justify-between">
          <p className="text-sm font-semibold uppercase tracking-wide text-brand">Nova corrida</p>
          <p className="tabular text-2xl font-bold">{seconds}s</p>
        </div>

        <p className="tabular mt-6 text-5xl font-bold">{brl(offer.fareCents)}</p>
        <p className="mt-1 text-muted">
          {offer.vehicleType === 'moto' ? 'Moto' : 'Carro'} · {km(offer.rideDistanceMeters)} ·{' '}
          {minutes(offer.rideDurationSeconds)}
        </p>

        <div className="mt-8 space-y-5">
          <div className="flex gap-3">
            <span className="mt-1.5 h-3 w-3 shrink-0 rounded-full bg-ink" />
            <div className="min-w-0">
              <p className="text-xs font-medium uppercase tracking-wide text-faint">Embarque</p>
              <p className="font-medium">{offer.originAddress}</p>
              <p className="tabular text-sm text-muted">
                {km(offer.distanceToPickupMeters)} · {minutes(offer.pickupEtaSeconds)} até lá
              </p>
            </div>
          </div>

          <div className="flex gap-3">
            <span className="mt-1.5 h-3 w-3 shrink-0 rounded-full bg-brand" />
            <div className="min-w-0">
              <p className="text-xs font-medium uppercase tracking-wide text-faint">Destino</p>
              <p className="font-medium">{offer.destinationAddress}</p>
            </div>
          </div>
        </div>

        {error && <p className="mt-6 text-sm text-danger">{error}</p>}
      </div>

      <div className="space-y-3 px-6 pb-[max(1.5rem,env(safe-area-inset-bottom))]">
        <Button
          size="lg"
          full
          loading={busy === 'accept'}
          disabled={busy !== null}
          onClick={() => void respond('accept')}
        >
          Aceitar corrida
        </Button>
        <Button
          variant="ghost"
          full
          loading={busy === 'decline'}
          disabled={busy !== null}
          onClick={() => void respond('decline')}
        >
          Recusar
        </Button>
      </div>
    </div>
  );
}
